import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
} from 'react';
import {
  WebChatClient,
  WebChatEvents,
  type ChatMessage,
  type ConversationStatus,
} from '@revocom/webchat-sdk';
import {
  ChannelLauncher,
  hasExternalChannels,
  resolveWidgetChannels,
} from './channels';
import {
  fetchPublicConfig,
  isPublicWidgetAvailable,
  resolveBootstrapApiUrl,
  resolveSessionApiUrl,
  websocketOriginFromEndpoint,
} from './api';
import { findEmbedScript } from './embed-script';
import { LocalStorageAdapter } from './storage';
import {
  bindNotificationSoundUnlock,
  messageNotificationSound,
} from './notification-sound';
import type { PublicWebChatConfig } from './types';
import {
  hasSeenPopupToday,
  isHiddenByDisplayMode,
  launcherIconSvg,
  launcherPixelSize,
  markPopupSeenToday,
  panelDimensions,
  resolveAppearance,
} from './appearance';
import { CloseGlyph } from './components/CloseGlyph';
import { ConversationStatusBanner } from './components/ConversationStatusBanner';
import { shouldShowWidgetBranding } from './branding';
import { PoweredBy } from './components/PoweredBy';
import { ResolvedConversationView } from './components/ResolvedConversationView';
import { VisitorInformationForm } from './components/VisitorInformationForm';
import { formatMessageTime } from './utils/formatMessageTime';
import {
  resolveVisitorInformationConfig,
  toInitProfile,
  type VisitorInformationConfig,
  type VisitorInformationValues,
} from './visitor-information';

const VISITOR_INFO_COLLECTED_KEY = 'visitorInfoCollected';
const VISITOR_INFO_PROFILE_KEY = 'visitorInfoProfile';
const UNAVAILABLE_TITLE = "We're currently unavailable";
const UNAVAILABLE_BODY =
  "We're unable to accept new conversations at the moment. Please try again later or contact us using another available method.";

type StoredVisitorProfile = {
  displayName?: string;
  email?: string;
  phoneNumber?: string;
};

function readStoredVisitorProfile(
  storage: LocalStorageAdapter,
): StoredVisitorProfile | undefined {
  const raw = storage.getItem(VISITOR_INFO_PROFILE_KEY);
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw) as StoredVisitorProfile;
    if (!parsed || typeof parsed !== 'object') return undefined;
    return {
      displayName:
        typeof parsed.displayName === 'string'
          ? parsed.displayName
          : undefined,
      email: typeof parsed.email === 'string' ? parsed.email : undefined,
      phoneNumber:
        typeof parsed.phoneNumber === 'string'
          ? parsed.phoneNumber
          : undefined,
    };
  } catch {
    return undefined;
  }
}

function isStoredVisitorProfileComplete(
  config: VisitorInformationConfig,
  profile: StoredVisitorProfile | undefined,
): boolean {
  if (!profile) return false;
  if (config.fields.name.enabled && config.fields.name.required) {
    if (!profile.displayName?.trim()) return false;
  }
  if (config.fields.email.enabled && config.fields.email.required) {
    if (!profile.email?.trim()) return false;
  }
  if (config.fields.phone.enabled && config.fields.phone.required) {
    if (!profile.phoneNumber?.trim()) return false;
  }
  // At least one enabled field must carry a value.
  const hasAny =
    (config.fields.name.enabled && Boolean(profile.displayName?.trim())) ||
    (config.fields.email.enabled && Boolean(profile.email?.trim())) ||
    (config.fields.phone.enabled && Boolean(profile.phoneNumber?.trim()));
  return hasAny;
}

function isVisitorInformationRequiredError(error: unknown): boolean {
  const message =
    error instanceof Error ? error.message.toLowerCase() : String(error);
  return (
    message.includes('name is required') ||
    message.includes('email is required') ||
    message.includes('phone is required') ||
    message.includes('email format is invalid') ||
    message.includes('phone format is invalid') ||
    message.includes('visitor information is required') ||
    message.includes('failed to associate customer') ||
    message.includes('lead conversation')
  );
}

function isPublicUnavailableError(error: unknown): boolean {
  const message =
    error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();
  return (
    message.includes("we're currently unavailable") ||
    message.includes("unable to accept new conversations at the moment") ||
    message.includes('webchat_unavailable')
  );
}

function isWidgetAvailabilityError(error: unknown): boolean {
  const message =
    error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();
  return (
    message.includes('widget_unavailable') ||
    message.includes('widget is unavailable') ||
    message.includes('subscription is inactive') ||
    message.includes('subscription expired')
  );
}

type UiMessage = {
  id: string;
  content: string;
  from: 'visitor' | 'agent' | 'system';
  status?: ChatMessage['status'];
  createdAt: Date;
  metadata?: Record<string, unknown> | null;
};

type ConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'lost';

type PendingBootContext = {
  apiUrl: string;
  websocketUrl: string;
  publicConfig: PublicWebChatConfig;
};

function isHistorySeparator(message: {
  from: string;
  metadata?: Record<string, unknown> | null;
}): boolean {
  return message.from === 'system' && message.metadata?.separator === true;
}

function formatClosedAt(value: unknown): string | null {
  if (typeof value !== 'string' && !(value instanceof Date)) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function statusLabel(status?: ChatMessage['status']): string {
  switch (status) {
    case 'READ':
      return 'Read';
    case 'DELIVERED':
      return 'Delivered';
    case 'SENT':
      return 'Sent';
    case 'PENDING':
      return 'Sending…';
    default:
      return '';
  }
}

function mapChatMessage(message: ChatMessage): UiMessage {
  const from: UiMessage['from'] =
    message.senderType === 'VISITOR'
      ? 'visitor'
      : message.senderType === 'SYSTEM'
        ? 'system'
        : 'agent';
  return {
    id: message.id,
    content: message.content,
    from,
    status: message.status,
    createdAt: new Date(message.createdAt),
    metadata: message.metadata ?? null,
  };
}

function upsertIncomingMessage(
  prev: UiMessage[],
  message: ChatMessage,
): UiMessage[] {
  const mapped = mapChatMessage(message);
  if (prev.some((m) => m.id === mapped.id)) {
    return prev;
  }

  const pendingIndex = prev.findIndex(
    (m) =>
      m.from === 'visitor' &&
      m.id.startsWith('pending-') &&
      m.content === mapped.content,
  );
  if (pendingIndex >= 0) {
    const next = [...prev];
    next[pendingIndex] = mapped;
    return next;
  }

  return [...prev, mapped];
}

function replacePendingMessage(
  prev: UiMessage[],
  pendingId: string,
  message: ChatMessage,
): UiMessage[] {
  const mapped = mapChatMessage(message);
  const withoutPending = prev.filter((m) => m.id !== pendingId);
  if (withoutPending.some((m) => m.id === mapped.id)) {
    return withoutPending;
  }
  return [...withoutPending, mapped];
}

export function ChatWidget({
  widgetId,
  previewConfig,
  inline = false,
  hideLauncher = false,
  panelOpen,
  onPanelOpenChange,
}: {
  widgetId: string;
  /** When set, skips network config fetch (dashboard live preview). */
  previewConfig?: PublicWebChatConfig;
  /** Render inside a container instead of fixed viewport corner. */
  inline?: boolean;
  /** When true, the parent launcher owns open/close. */
  hideLauncher?: boolean;
  panelOpen?: boolean;
  onPanelOpenChange?: (open: boolean) => void;
}) {
  const [config, setConfig] = useState<PublicWebChatConfig | null>(
    previewConfig ?? null,
  );
  const [bootError, setBootError] = useState<string | null>(null);
  const [open, setOpen] = useState(Boolean(previewConfig?.openAutomatically));
  const [minimized, setMinimized] = useState(false);
  const [draft, setDraft] = useState('');
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [agentTyping, setAgentTyping] = useState(false);
  const [connection, setConnection] = useState<ConnectionState>('connecting');
  const [unread, setUnread] = useState(0);
  const [ready, setReady] = useState(Boolean(previewConfig));
  const [conversationStatus, setConversationStatus] =
    useState<ConversationStatus>('OPEN');
  const [startingNew, setStartingNew] = useState(false);
  const [needsVisitorInfo, setNeedsVisitorInfo] = useState(false);
  const [visitorInfoSubmitting, setVisitorInfoSubmitting] = useState(false);
  const [visitorInfoError, setVisitorInfoError] = useState<string | null>(null);
  const [showPopup, setShowPopup] = useState(false);
  const [hiddenByDisplay, setHiddenByDisplay] = useState(false);
  const [publicUnavailable, setPublicUnavailable] = useState(false);
  const [availabilityBlocked, setAvailabilityBlocked] = useState(false);
  const [channelsMenuOpen, setChannelsMenuOpen] = useState(false);

  const clientRef = useRef<WebChatClient | null>(null);
  const openRef = useRef(open);
  const listRef = useRef<HTMLDivElement | null>(null);
  const visitorTypingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const visitorTypingActiveRef = useRef(false);
  const agentTypingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const soundEnabledRef = useRef(true);
  const pendingBootRef = useRef<PendingBootContext | null>(null);
  const storageRef = useRef<LocalStorageAdapter | null>(null);
  const bootGenerationRef = useRef(0);

  openRef.current = open;
  soundEnabledRef.current = config?.soundEnabled !== false;

  useEffect(() => {
    if (!hideLauncher || panelOpen === undefined) {
      return;
    }
    if (panelOpen) {
      setOpen(true);
      setMinimized(false);
      setChannelsMenuOpen(false);
      setUnread(0);
      return;
    }
    setOpen(false);
    setMinimized(false);
  }, [hideLauncher, panelOpen]);

  const appearance = useMemo(() => resolveAppearance(config), [config]);
  const widgetChannels = useMemo(
    () => resolveWidgetChannels(config?.channels),
    [config?.channels],
  );
  const multiChannel = hasExternalChannels(widgetChannels);

  useEffect(() => {
    return bindNotificationSoundUnlock();
  }, []);

  useEffect(() => {
    if (!config) return;
    const updateDisplay = () => {
      setHiddenByDisplay(isHiddenByDisplayMode(appearance.displayMode));
    };
    updateDisplay();
    const mq = window.matchMedia('(max-width: 768px)');
    mq.addEventListener('change', updateDisplay);
    return () => mq.removeEventListener('change', updateDisplay);
  }, [config, appearance.displayMode]);

  useEffect(() => {
    if (!config || previewConfig || open || minimized || channelsMenuOpen) {
      setShowPopup(false);
      return;
    }
    const message = appearance.popupMessage?.trim();
    if (!message || hasSeenPopupToday(config.publicKey)) {
      setShowPopup(false);
      return;
    }
    const timer = window.setTimeout(() => setShowPopup(true), 2500);
    return () => window.clearTimeout(timer);
  }, [
    config,
    previewConfig,
    open,
    minimized,
    channelsMenuOpen,
    appearance.popupMessage,
  ]);

  const themeVars = useMemo(() => {
    if (!config) return {};
    const dark =
      config.theme === 'dark' ||
      (config.theme === 'auto' &&
        typeof window !== 'undefined' &&
        window.matchMedia('(prefers-color-scheme: dark)').matches);
    const panel = panelDimensions(appearance.widgetSize);
    const launcherPx = launcherPixelSize(appearance.launcherSize);
    const headerStart = appearance.headerBackground || config.primaryColor;
    const headerEnd = config.secondaryColor;

    return {
      '--rw-primary': config.primaryColor,
      '--rw-secondary': config.secondaryColor,
      '--rw-header-gradient': `linear-gradient(135deg, ${headerStart}, ${headerEnd})`,
      '--rw-surface': dark ? '#0f172a' : '#ffffff',
      '--rw-ink': dark ? '#f8fafc' : appearance.textColor || '#0f172a',
      '--rw-muted': dark ? '#94a3b8' : '#64748b',
      '--rw-border': dark ? 'rgba(148,163,184,0.25)' : 'rgba(15,23,42,0.1)',
      '--rw-panel': dark
        ? '#1e293b'
        : appearance.chatBackground || '#f8fafc',
      '--rw-header-text': appearance.headerTextColor || '#ffffff',
      '--rw-button': appearance.buttonPrimaryColor || config.primaryColor,
      '--rw-button-hover':
        appearance.buttonHoverColor || config.secondaryColor,
      '--rw-radius': `${appearance.borderRadius}px`,
      '--rw-bubble-radius': `${appearance.messageBubbleRadius}px`,
      '--rw-button-radius': `${appearance.buttonRadius}px`,
      '--rw-panel-width': `${panel.width}px`,
      '--rw-panel-height': `${panel.height}px`,
      '--rw-launcher-size': `${launcherPx}px`,
      '--rw-offset-x': `${appearance.offsetX}px`,
      '--rw-offset-y': `${appearance.offsetY}px`,
      '--rw-font-family': appearance.fontFamily,
      '--rw-font-size': `${appearance.fontSize}px`,
      '--rw-font-weight': String(appearance.fontWeight),
      '--rw-shadow': appearance.shadow
        ? '0 24px 60px rgba(15, 23, 42, 0.28)'
        : 'none',
    } as CSSProperties;
  }, [config, appearance]);

  const positionClass =
    config?.position === 'bottom-left' ? 'rw-pos-left' : 'rw-pos-right';
  const animationClass =
    !(open && !minimized) && appearance.launcherAnimation === 'bounce'
      ? 'rw-launcher-bounce'
      : !(open && !minimized) && appearance.launcherAnimation === 'pulse'
        ? 'rw-launcher-pulse'
        : '';
  const rootClass = [
    'rw-root',
    hideLauncher && !inline ? 'rw-embedded' : '',
    inline ? 'rw-inline' : hideLauncher ? '' : positionClass,
    hiddenByDisplay ? 'rw-hidden-display' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const clearAgentTypingState = useCallback(() => {
    if (agentTypingTimerRef.current) {
      clearTimeout(agentTypingTimerRef.current);
      agentTypingTimerRef.current = null;
    }
    setAgentTyping(false);
  }, []);

  const scheduleAgentTypingExpiry = useCallback(() => {
    if (agentTypingTimerRef.current) {
      clearTimeout(agentTypingTimerRef.current);
    }
    agentTypingTimerRef.current = setTimeout(() => {
      setAgentTyping(false);
      agentTypingTimerRef.current = null;
    }, 3000);
  }, []);

  const stopVisitorTyping = useCallback(() => {
    if (visitorTypingTimerRef.current) {
      clearTimeout(visitorTypingTimerRef.current);
      visitorTypingTimerRef.current = null;
    }
    if (!visitorTypingActiveRef.current) {
      return;
    }
    visitorTypingActiveRef.current = false;
    const client = clientRef.current;
    if (!client || previewConfig) return;
    console.debug('[typing][widget] emit typing:stopped');
    void client.stopTyping().catch(() => undefined);
  }, [previewConfig]);

  const bindClientEvents = useCallback((client: WebChatClient) => {
    client.on(WebChatEvents.CONNECTED, () => {
      setConnection('connected');
      clearAgentTypingState();
    });
    client.on(WebChatEvents.RECONNECTED, () => {
      setConnection('connected');
      clearAgentTypingState();
    });
    client.on(WebChatEvents.DISCONNECTED, () => {
      setConnection('lost');
      clearAgentTypingState();
    });
    client.on(WebChatEvents.ERROR, ({ error }) => {
      if (isWidgetAvailabilityError(error)) {
        setAvailabilityBlocked(true);
        setConfig(null);
        setOpen(false);
        setShowPopup(false);
        clientRef.current?.disconnect();
        clientRef.current = null;
        return;
      }
      setConnection('lost');
      clearAgentTypingState();
    });

    client.on(WebChatEvents.MESSAGE_CREATED, ({ message }) => {
      const from: UiMessage['from'] =
        message.senderType === 'VISITOR'
          ? 'visitor'
          : message.senderType === 'SYSTEM'
            ? 'system'
            : 'agent';
      setMessages((prev) => upsertIncomingMessage(prev, message));

      if (from === 'agent') {
        if (soundEnabledRef.current) {
          void messageNotificationSound.play();
        }
        if (!openRef.current) {
          setUnread((n) => n + 1);
        } else {
          void client.markRead(message.id).catch(() => undefined);
        }
        void client.markDelivered(message.id).catch(() => undefined);
      }
    });

    client.on(WebChatEvents.MESSAGE_DELIVERED, ({ messageId, status }) => {
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, status } : m)),
      );
    });

    client.on(WebChatEvents.MESSAGE_READ, ({ messageId, status }) => {
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, status } : m)),
      );
    });

    client.on(WebChatEvents.TYPING_STARTED, ({ participantType }) => {
      console.debug('[typing][widget] received typing:started', { participantType });
      if (participantType === 'VISITOR') return;
      setAgentTyping(true);
      scheduleAgentTypingExpiry();
    });
    client.on(WebChatEvents.TYPING_STOPPED, ({ participantType }) => {
      console.debug('[typing][widget] received typing:stopped', { participantType });
      if (participantType === 'VISITOR') return;
      clearAgentTypingState();
    });

    client.on(
      WebChatEvents.CONVERSATION_STATUS_UPDATED,
      ({ conversationId, status }) => {
        const activeId = client.conversationId;
        if (activeId && conversationId !== activeId) return;
        setConversationStatus(status);
      },
    );
  }, [clearAgentTypingState, scheduleAgentTypingExpiry]);

  const startRealtimeSession = useCallback(
    async (
      boot: PendingBootContext,
      profile?: {
        displayName?: string;
        email?: string;
        phoneNumber?: string;
      },
    ) => {
      const generation = bootGenerationRef.current;
      const storage = storageRef.current ?? new LocalStorageAdapter(widgetId);
      storageRef.current = storage;

      stopVisitorTyping();
      clearAgentTypingState();
      clientRef.current?.disconnect();
      clientRef.current = null;

      const client = new WebChatClient({
        apiUrl: boot.apiUrl,
        websocketUrl: boot.websocketUrl,
        publicKey: boot.publicConfig.publicKey,
        storage,
        displayName: profile?.displayName,
        email: profile?.email,
        phoneNumber: profile?.phoneNumber,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10_000,
      });
      clientRef.current = client;
      bindClientEvents(client);

      setConnection('connecting');
      setPublicUnavailable(false);
      await client.start();
      if (generation !== bootGenerationRef.current) return;

      const session = client.session;
      const visitorInfoEnabled = resolveVisitorInformationConfig(
        boot.publicConfig.visitorInformation,
      ).enabled;
      if (visitorInfoEnabled && !session?.conversationId) {
        throw new Error(
          'Visitor information was submitted but no conversation was created.',
        );
      }

      if (session?.conversationStatus) {
        setConversationStatus(session.conversationStatus);
      } else {
        setConversationStatus(visitorInfoEnabled ? 'PENDING' : 'OPEN');
      }
      if (session?.initialMessages?.length) {
        setMessages((prev) => {
          const welcome = prev.filter((m) => m.id === 'welcome');
          const history = session.initialMessages!.map(mapChatMessage);
          const seen = new Set<string>();
          return [...welcome, ...history].filter((m) => {
            if (seen.has(m.id)) return false;
            seen.add(m.id);
            return true;
          });
        });
      }
      setNeedsVisitorInfo(false);
      setConnection('connected');
    },
    [bindClientEvents, clearAgentTypingState, stopVisitorTyping, widgetId],
  );

  useEffect(() => {
    if (previewConfig) {
      setConfig(previewConfig);
      setReady(true);
      setConnection('connected');
      setNeedsVisitorInfo(
        resolveVisitorInformationConfig(previewConfig.visitorInformation)
          .enabled,
      );
      setMessages([
        {
          id: 'welcome',
          content: previewConfig.welcomeMessage,
          from: 'system',
          createdAt: new Date(),
        },
      ]);
      return;
    }

    let cancelled = false;
    const generation = ++bootGenerationRef.current;

    async function boot() {
      try {
        setBootError(null);
        setVisitorInfoError(null);
        setConnection('connecting');
        const embedScript = findEmbedScript();
        const bootstrapApi = resolveBootstrapApiUrl(embedScript);
        const publicConfigResponse = await fetchPublicConfig(
          bootstrapApi,
          widgetId,
        );
        if (cancelled || generation !== bootGenerationRef.current) return;
        if (!isPublicWidgetAvailable(publicConfigResponse)) {
          setAvailabilityBlocked(true);
          setConfig(null);
          setReady(true);
          return;
        }
        const publicConfig = publicConfigResponse.config;
        if (!publicConfig.enabled) {
          setAvailabilityBlocked(true);
          setConfig(null);
          setReady(true);
          return;
        }

        setConfig(publicConfig);
        setMessages([
          {
            id: 'welcome',
            content: publicConfig.welcomeMessage,
            from: 'system',
            createdAt: new Date(),
          },
        ]);
        if (publicConfig.openAutomatically) {
          const channels = resolveWidgetChannels(publicConfig.channels);
          if (hasExternalChannels(channels)) {
            setChannelsMenuOpen(true);
          } else {
            setOpen(true);
          }
        }
        setReady(true);

        const apiUrl = resolveSessionApiUrl(
          publicConfig.apiBaseUrl,
          embedScript,
        );
        const websocketUrl = websocketOriginFromEndpoint(
          publicConfig.websocketEndpoint,
        );
        const bootContext: PendingBootContext = {
          apiUrl,
          websocketUrl,
          publicConfig,
        };
        pendingBootRef.current = bootContext;

        const storage = new LocalStorageAdapter(widgetId);
        storageRef.current = storage;
        const visitorInfo = resolveVisitorInformationConfig(
          publicConfig.visitorInformation,
        );
        const storedProfile = readStoredVisitorProfile(storage);
        const alreadyCollected =
          storage.getItem(VISITOR_INFO_COLLECTED_KEY) === '1';

        if (visitorInfo.enabled) {
          if (
            !alreadyCollected ||
            !isStoredVisitorProfileComplete(visitorInfo, storedProfile)
          ) {
            storage.removeItem(VISITOR_INFO_COLLECTED_KEY);
            setNeedsVisitorInfo(true);
            setConnection('connected');
            return;
          }

          try {
            await startRealtimeSession(bootContext, storedProfile);
          } catch (error) {
            if (isVisitorInformationRequiredError(error)) {
              storage.removeItem(VISITOR_INFO_COLLECTED_KEY);
              storage.removeItem(VISITOR_INFO_PROFILE_KEY);
              setNeedsVisitorInfo(true);
              setVisitorInfoError(
                error instanceof Error
                  ? error.message
                  : 'Please complete your details to continue.',
              );
              setConnection('connected');
              return;
            }
            throw error;
          }
          return;
        }

        await startRealtimeSession(bootContext);
      } catch (error) {
        if (cancelled || generation !== bootGenerationRef.current) return;
        if (isWidgetAvailabilityError(error)) {
          setAvailabilityBlocked(true);
          setConfig(null);
          setBootError(null);
          setReady(true);
          return;
        }
        if (isPublicUnavailableError(error)) {
          setPublicUnavailable(true);
          setConnection('connected');
          setBootError(null);
          setReady(true);
          return;
        }
        setAvailabilityBlocked(true);
        setConfig(null);
        setBootError(null);
        setReady(true);
      }
    }

    void boot();

    return () => {
      cancelled = true;
      bootGenerationRef.current += 1;
      stopVisitorTyping();
      clearAgentTypingState();
      clientRef.current?.disconnect();
      clientRef.current = null;
    };
  }, [
    widgetId,
    previewConfig,
    startRealtimeSession,
    stopVisitorTyping,
    clearAgentTypingState,
  ]);

  useEffect(() => {
    if (previewConfig) {
      return;
    }
    let cancelled = false;

    async function refreshVisibility() {
      try {
        const embedScript = findEmbedScript();
        const bootstrapApi = resolveBootstrapApiUrl(embedScript);
        const publicConfigResponse = await fetchPublicConfig(
          bootstrapApi,
          widgetId,
        );
        if (cancelled) return;
        if (
          !isPublicWidgetAvailable(publicConfigResponse) ||
          !publicConfigResponse.config.enabled
        ) {
          setAvailabilityBlocked(true);
          setConfig(null);
          setChannelsMenuOpen(false);
          return;
        }
        setAvailabilityBlocked(false);
        const next = publicConfigResponse.config;
        setConfig((current) =>
          current
            ? { ...current, channels: next.channels, enabled: next.enabled }
            : next,
        );
      } catch {
        return;
      }
    }

    const interval = window.setInterval(() => void refreshVisibility(), 8_000);
    function onVisible() {
      if (document.visibilityState === 'visible') {
        void refreshVisibility();
      }
    }
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [widgetId, previewConfig]);

  useEffect(() => {
    if (!previewConfig) return;
    setConfig(previewConfig);
    if (previewConfig.openAutomatically) {
      setOpen(true);
      setMinimized(false);
    }
    setMessages((prev) => {
      const withoutWelcome = prev.filter((m) => m.id !== 'welcome');
      return [
        {
          id: 'welcome',
          content: previewConfig.welcomeMessage,
          from: 'system',
          createdAt: new Date(),
        },
        ...withoutWelcome,
      ];
    });
  }, [previewConfig]);

  useEffect(() => {
    listRef.current?.scrollTo({
      top: listRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages, agentTyping, open]);

  useEffect(() => {
    if (!open || minimized || conversationStatus === 'CLOSED') {
      stopVisitorTyping();
    }
    if (conversationStatus === 'CLOSED') {
      clearAgentTypingState();
    }
  }, [
    open,
    minimized,
    conversationStatus,
    stopVisitorTyping,
    clearAgentTypingState,
  ]);

  const toggleOpen = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      if (next) {
        setMinimized(false);
        setUnread(0);
        setChannelsMenuOpen(false);
      } else {
        stopVisitorTyping();
        clearAgentTypingState();
      }
      return next;
    });
  }, [clearAgentTypingState, stopVisitorTyping]);

  const openChatPanel = useCallback(() => {
    messageNotificationSound.unlock();
    setChannelsMenuOpen(false);
    setMinimized(false);
    setOpen(true);
    setUnread(0);
    setShowPopup(false);
    if (config?.publicKey) markPopupSeenToday(config.publicKey);
  }, [config?.publicKey]);

  const closeAll = useCallback(() => {
    stopVisitorTyping();
    clearAgentTypingState();
    setOpen(false);
    setMinimized(false);
    setChannelsMenuOpen(false);
    onPanelOpenChange?.(false);
  }, [clearAgentTypingState, onPanelOpenChange, stopVisitorTyping]);

  const hidePublicWidget = useCallback(() => {
    stopVisitorTyping();
    clearAgentTypingState();
    clientRef.current?.disconnect();
    clientRef.current = null;
    pendingBootRef.current = null;
    setAvailabilityBlocked(true);
    setConfig(null);
    setOpen(false);
    setMinimized(false);
    setChannelsMenuOpen(false);
    setShowPopup(false);
    setBootError(null);
    setVisitorInfoError(null);
    setReady(true);
  }, [clearAgentTypingState, stopVisitorTyping]);

  async function onVisitorInfoSubmit(values: VisitorInformationValues) {
    if (visitorInfoSubmitting) return;

    if (previewConfig) {
      setNeedsVisitorInfo(false);
      return;
    }

    const boot = pendingBootRef.current;
    const publicConfig = config;
    if (!boot || !publicConfig) return;

    const visitorInfo = resolveVisitorInformationConfig(
      publicConfig.visitorInformation,
    );
    const profile = toInitProfile(visitorInfo, values);

    setVisitorInfoSubmitting(true);
    setVisitorInfoError(null);
    setBootError(null);
    setPublicUnavailable(false);

    try {
      await startRealtimeSession(boot, profile);
      storageRef.current?.setItem(VISITOR_INFO_COLLECTED_KEY, '1');
      storageRef.current?.setItem(
        VISITOR_INFO_PROFILE_KEY,
        JSON.stringify(profile),
      );
    } catch (error) {
      if (isWidgetAvailabilityError(error)) {
        hidePublicWidget();
        return;
      }
      if (isPublicUnavailableError(error)) {
        setPublicUnavailable(true);
        setConnection('connected');
        setVisitorInfoError(null);
        return;
      }
      setVisitorInfoError(
        error instanceof Error
          ? error.message
          : 'Failed to start the conversation.',
      );
      setConnection('lost');
    } finally {
      setVisitorInfoSubmitting(false);
    }
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const content = draft.trim();
    if (!content) return;
    if (conversationStatus === 'CLOSED') return;

    if (previewConfig) {
      setMessages((prev) => [
        ...prev,
        {
          id: `local-${Date.now()}`,
          content,
          from: 'visitor',
          status: 'SENT',
          createdAt: new Date(),
        },
      ]);
      setDraft('');
      return;
    }

    const client = clientRef.current;
    if (!client) return;

    stopVisitorTyping();
    const pendingId = `pending-${Date.now()}`;
    setDraft('');
    setMessages((prev) => [
      ...prev,
      {
        id: pendingId,
        content,
        from: 'visitor',
        status: 'PENDING',
        createdAt: new Date(),
      },
    ]);

    try {
      const result = await client.sendMessage(content);
      if (result.status) {
        setConversationStatus(result.status);
      } else if (result.conversationCreated || result.conversationReopened) {
        setConversationStatus('OPEN');
      }
      setMessages((prev) =>
        replacePendingMessage(prev, pendingId, result.message),
      );
    } catch (error) {
      setMessages((prev) => prev.filter((m) => m.id !== pendingId));
      setDraft(content);
      if (isWidgetAvailabilityError(error)) {
        hidePublicWidget();
        return;
      }
      if (isPublicUnavailableError(error)) {
        setPublicUnavailable(true);
        setBootError(null);
        return;
      }
      setBootError(
        error instanceof Error ? error.message : 'Failed to send message.',
      );
    }
  }

  function onDraftChange(value: string) {
    setDraft(value);
    if (conversationStatus === 'CLOSED') return;
    const client = clientRef.current;
    if (!client || previewConfig) return;

    const hasContent = value.trim().length > 0;
    if (!hasContent) {
      stopVisitorTyping();
      return;
    }

    if (!visitorTypingActiveRef.current) {
      visitorTypingActiveRef.current = true;
      console.debug('[typing][widget] emit typing:started');
      void client.startTyping().catch(() => undefined);
    }

    if (visitorTypingTimerRef.current) clearTimeout(visitorTypingTimerRef.current);
    visitorTypingTimerRef.current = setTimeout(() => {
      console.debug('[typing][widget] emit typing:stopped (idle)');
      stopVisitorTyping();
    }, 1200);
  }

  async function onStartNewConversation() {
    stopVisitorTyping();
    clearAgentTypingState();
    if (previewConfig) {
      setConversationStatus('OPEN');
      return;
    }

    const client = clientRef.current;
    if (!client || startingNew) return;

    setStartingNew(true);
    setBootError(null);
    try {
      const result = await client.reopenConversation();
      setConversationStatus(result.status);
      if (result.separatorMessage) {
        setMessages((prev) =>
          upsertIncomingMessage(prev, result.separatorMessage!),
        );
      }
    } catch (error) {
      if (isWidgetAvailabilityError(error)) {
        hidePublicWidget();
        return;
      }
      setBootError(
        error instanceof Error
          ? error.message
          : 'Failed to start a new conversation.',
      );
    } finally {
      setStartingNew(false);
    }
  }

  const isResolved = conversationStatus === 'CLOSED';
  const messagingDisabled = connection === 'lost' && !previewConfig;

  if (availabilityBlocked && !previewConfig) {
    return null;
  }

  if (!previewConfig && (!config || !ready)) {
    return null;
  }

  if (bootError && !config) {
    return null;
  }

  if (!config || !ready) {
    return null;
  }

  if (!previewConfig && !hideLauncher && widgetChannels.length === 0) {
    return null;
  }

  const visitorInfo = resolveVisitorInformationConfig(
    config.visitorInformation,
  );
  const showVisitorForm = needsVisitorInfo && visitorInfo.enabled;
  const title =
    appearance.companyName?.trim() ||
    config.name ||
    config.organization.name;
  const subtitle =
    appearance.subtitle?.trim() ||
    (showVisitorForm
      ? 'Welcome'
      : connection === 'connected'
        ? 'Online'
        : connection === 'connecting' || connection === 'reconnecting'
          ? 'Connecting…'
          : 'Connection lost');
  const avatar = config.avatar || config.logo || config.organization.logo;
  const customLauncherIcon =
    appearance.launcherIconKey === 'custom'
      ? appearance.launcherIconUrl || config.launcherIcon || null
      : null;
  const popupText = appearance.popupMessage?.trim() || '';
  const organizationPhone = config.organization.phone?.trim() || null;
  const organizationWebsite = config.organization.website?.trim() || null;
  const showPoweredBy = shouldShowWidgetBranding(config);
  const showLauncherWatermark = showPoweredBy && !hideLauncher;

  function dismissPopup() {
    if (config?.publicKey) markPopupSeenToday(config.publicKey);
    setShowPopup(false);
  }

  function openChat() {
    messageNotificationSound.unlock();
    dismissPopup();
    if (minimized) {
      setMinimized(false);
      setOpen(true);
      setUnread(0);
      setChannelsMenuOpen(false);
      return;
    }
    if (multiChannel && !open) {
      setChannelsMenuOpen((prev) => !prev);
      return;
    }
    toggleOpen();
  }

  return (
    <div
      className={[rootClass, showLauncherWatermark ? 'rw-has-watermark' : '']
        .filter(Boolean)
        .join(' ')}
      style={themeVars}
    >
      <div
        className={[
          'rw-panel',
          open && !minimized ? 'rw-panel-open' : '',
          minimized ? 'rw-panel-minimized' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-hidden={!open || minimized}
      >
        <header className="rw-header">
          <div className="rw-header-main">
            {avatar ? (
              <img className="rw-avatar" src={avatar} alt="" />
            ) : (
              <div className="rw-avatar rw-avatar-fallback">
                {title.slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="rw-header-text">
              <div className="rw-title">{title}</div>
              <div className="rw-subtitle">{subtitle}</div>
            </div>
          </div>
          <div className="rw-header-actions">
            <button
              type="button"
              className="rw-icon-btn"
              aria-label="Minimize"
              onClick={() => setMinimized(true)}
            >
              ─
            </button>
            <button
              type="button"
              className="rw-icon-btn"
              aria-label="Close"
              onClick={() => {
                closeAll();
              }}
            >
              <CloseGlyph />
            </button>
          </div>
        </header>

        {showVisitorForm ? (
          <VisitorInformationForm
            config={visitorInfo}
            submitting={visitorInfoSubmitting}
            error={visitorInfoError}
            onSubmit={(values) => void onVisitorInfoSubmit(values)}
          />
        ) : publicUnavailable ? (
          <div className="rw-unavailable" role="status" aria-live="polite">
            <h3 className="rw-unavailable-title">{UNAVAILABLE_TITLE}</h3>
            <p className="rw-unavailable-body">{UNAVAILABLE_BODY}</p>
            {organizationPhone || organizationWebsite ? (
              <div className="rw-unavailable-contacts">
                {organizationPhone ? (
                  <a className="rw-unavailable-contact" href={`tel:${organizationPhone}`}>
                    {organizationPhone}
                  </a>
                ) : null}
                {organizationWebsite ? (
                  <a
                    className="rw-unavailable-contact"
                    href={organizationWebsite}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {organizationWebsite}
                  </a>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : (
          <>
            {connection === 'lost' && (
              <div className="rw-banner">Connection lost — reconnecting…</div>
            )}

            {!previewConfig && conversationStatus !== 'CLOSED' && (
              <ConversationStatusBanner status={conversationStatus} />
            )}

            <div className="rw-messages" ref={listRef}>
              {messages.map((message) => {
                if (isHistorySeparator(message)) {
                  const label =
                    typeof message.metadata?.label === 'string'
                      ? message.metadata.label
                      : message.content || 'History';
                  const closedOn = formatClosedAt(message.metadata?.closedAt);
                  return (
                    <div key={message.id} className="rw-history-divider">
                      <hr className="rw-history-divider-rule" />
                      <div className="rw-history-divider-label">{label}</div>
                      {closedOn ? (
                        <div className="rw-history-divider-meta">
                          Previous discussion closed on {closedOn}
                        </div>
                      ) : null}
                      <hr className="rw-history-divider-rule" />
                    </div>
                  );
                }

                if (message.from === 'system') {
                  return (
                    <div
                      key={message.id}
                      className="rw-bubble rw-bubble-system"
                      role="status"
                    >
                      <div className="rw-bubble-content">{message.content}</div>
                      <div className="rw-timestamp">
                        {formatMessageTime(message.createdAt)}
                      </div>
                    </div>
                  );
                }

                return (
                  <div
                    key={message.id}
                    className={`rw-bubble rw-bubble-${message.from}`}
                  >
                    <div className="rw-bubble-content">{message.content}</div>
                    <div className="rw-timestamp">
                      {formatMessageTime(message.createdAt)}
                    </div>
                    {message.from === 'visitor' && message.status && (
                      <div className="rw-receipt">
                        {statusLabel(message.status)}
                      </div>
                    )}
                  </div>
                );
              })}
              {agentTyping && config.typingIndicator !== false && (
                <div className="rw-bubble rw-bubble-agent rw-typing">
                  <span />
                  <span />
                  <span />
                </div>
              )}
            </div>

            {isResolved ? (
              <ResolvedConversationView
                onStartNew={() => void onStartNewConversation()}
                starting={startingNew || messagingDisabled}
              />
            ) : (
              <form className="rw-composer" onSubmit={(e) => void onSubmit(e)}>
                <input
                  value={draft}
                  onChange={(e) => onDraftChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void onSubmit(e);
                    }
                  }}
                  placeholder="Type a message…"
                  aria-label="Message"
                  disabled={messagingDisabled}
                />
                <button
                  type="submit"
                  disabled={messagingDisabled || !draft.trim()}
                >
                  Send
                </button>
              </form>
            )}
            {showPoweredBy && <PoweredBy />}
          </>
        )}
      </div>

      {showPopup && popupText && !(open && !minimized) && !channelsMenuOpen ? (
        <div className="rw-popup" role="dialog" aria-label="Chat invitation">
          <button
            type="button"
            className="rw-popup-close"
            aria-label="Dismiss"
            onClick={dismissPopup}
          >
            ✕
          </button>
          <button type="button" className="rw-popup-body" onClick={openChat}>
            {popupText}
          </button>
        </div>
      ) : null}

      {hideLauncher ? null : multiChannel ? (
        <ChannelLauncher
          channels={widgetChannels}
          open={channelsMenuOpen}
          chatOpen={open && !minimized}
          unread={unread}
          customLauncherIcon={customLauncherIcon}
          launcherIconHtml={launcherIconSvg(appearance.launcherIconKey)}
          animationClass={animationClass}
          onToggleMenu={() => {
            messageNotificationSound.unlock();
            dismissPopup();
            setChannelsMenuOpen(true);
          }}
          onOpenWebChat={openChatPanel}
          onClose={closeAll}
        />
      ) : (
        <div
          className={['rw-launcher-motion', animationClass]
            .filter(Boolean)
            .join(' ')}
        >
          <button
            type="button"
            className={[
              'rw-launcher',
              open && !minimized ? 'rw-launcher-open' : '',
              customLauncherIcon ? 'rw-launcher-image' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={openChat}
            aria-label={open ? 'Close chat' : 'Open chat'}
            style={
              customLauncherIcon && !(open && !minimized)
                ? {
                    backgroundImage: `url(${customLauncherIcon})`,
                    backgroundSize: 'cover',
                  }
                : undefined
            }
          >
            {customLauncherIcon && !(open && !minimized) ? null : open &&
              !minimized ? (
              <CloseGlyph />
            ) : (
              <span
                className="rw-launcher-icon"
                dangerouslySetInnerHTML={{
                  __html: launcherIconSvg(appearance.launcherIconKey),
                }}
              />
            )}
            {unread > 0 && !open && (
              <span className="rw-badge">{unread > 9 ? '9+' : unread}</span>
            )}
          </button>
        </div>
      )}
      {showLauncherWatermark ? <PoweredBy variant="launcher" /> : null}
    </div>
  );
}
